/** Operações em massa sobre os dados de um IFC: acrescentar ou alterar
 *  propriedades (vindas de uma folha de Excel), mudar o nome de Psets e
 *  de propriedades, e apagar Psets e propriedades.
 *
 *  As ideias vêm do liteAECO (Inject Properties, Pset Renamer, Pset
 *  Delete; Apache-2.0) e o cuidado com os Psets partilhados do IFClite
 *  (@ifc-lite/export, "copies a shared property set on write"; MPL-2.0).
 *  O código é nosso, sobre o ModeloStep.
 *
 *  Três regras que não se negociam:
 *
 *  1. **Um Pset partilhado copia-se antes de mudar.** O mesmo
 *     IfcPropertySet pode estar ligado a cem paredes. Mudar o valor de
 *     uma parede no Pset partilhado mudava as cem. Quando a alteração é
 *     só para parte dos elementos, o elemento recebe uma cópia do Pset
 *     e a cópia é que muda. O mesmo para uma propriedade partilhada por
 *     vários Psets.
 *
 *  2. **O tipo do valor não se adivinha.** Um "3,5" pode ser IfcReal,
 *     IfcLengthMeasure ou IfcLabel, e isso muda o que o valor quer
 *     dizer. Por ordem: o tipo escrito no cabeçalho da coluna; o tipo
 *     que a propriedade já tem nesse elemento; o único tipo que o resto
 *     do modelo usa para ela; e, se não houver nada disso, IfcLabel
 *     (texto). Se o modelo usar dois tipos diferentes, não se escolhe:
 *     a operação é recusada e pede-se o tipo no cabeçalho. O relatório
 *     diz, para cada propriedade, que tipo ficou e porquê.
 *
 *  3. **Nada fica pendurado.** Apagar passa pelo ModeloStep.apagar, que
 *     limpa as referências ou recusa.
 */

import type { ModeloStep } from '@viewer/lib/step/modeloStep'
import {
  argTexto,
  lerArgTexto,
  lerListaDeRefs,
  lerRef,
  lerValorTipado,
  novoGlobalId,
} from '@viewer/lib/step/textoStep'
import { splitTopLevelArgs, stepReal } from '@viewer/lib/stepText'
import {
  associarMaterial,
  classificar,
  construirIndiceDeAssociacoes,
  type IndiceDeAssociacoes,
} from './associacoes'

export type Operacao =
  | {
      tipo: 'definir'
      guid: string
      pset: string
      propriedade: string
      valor: string
      /** Tipo pedido explicitamente (ex.: IFCLENGTHMEASURE). */
      tipoDeValor?: string
      /** Forma de uma propriedade NOVA. Uma que já exista mantém a sua. */
      forma?: FormaDaPropriedade
      /** Classe de uma quantidade nova (ex.: IFCQUANTITYAREA). */
      classeDaQuantidade?: string
    }
  | { tipo: 'material'; guid: string; material: string }
  | { tipo: 'classificar'; guid: string; sistema: string; codigo: string; nome?: string }
  | { tipo: 'renomear-pset'; de: string; para: string }
  | { tipo: 'renomear-propriedade'; pset: string; de: string; para: string }
  | { tipo: 'apagar-pset'; nome: string }
  | { tipo: 'apagar-propriedade'; pset: string; nome: string }

export type FormaDaPropriedade = 'simples' | 'lista' | 'intervalo' | 'quantidade'

export const CLASSES_DE_QUANTIDADE = [
  'IFCQUANTITYLENGTH', 'IFCQUANTITYAREA', 'IFCQUANTITYVOLUME', 'IFCQUANTITYCOUNT',
  'IFCQUANTITYWEIGHT', 'IFCQUANTITYTIME', 'IFCQUANTITYNUMBER',
]

/** "2..5", "2..", "..5" -> limites; null se não for um intervalo. */
export function lerIntervalo(s: string): { min: string | null; max: string | null } | null {
  const m = /^\s*(.*?)\s*\.\.\s*(.*?)\s*$/.exec(s)
  if (!m || (m[1] === '' && m[2] === '')) return null
  return { min: m[1] === '' ? null : m[1], max: m[2] === '' ? null : m[2] }
}

/** Um conjunto de operações que se anula de uma vez (uma importação,
 *  um renomear). */
export interface Lote {
  id: string
  descricao: string
  quando: string
  operacoes: Operacao[]
}

export interface TipoEscolhido {
  pset: string
  propriedade: string
  tipo: string
  porque: 'cabecalho' | 'existente' | 'modelo' | 'omissao' | 'enumeracao'
}

export interface Relatorio {
  aplicadas: number
  ignoradas: { operacao: Operacao; motivo: string }[]
  psetsCriados: number
  propriedadesCriadas: number
  valoresAlterados: number
  copiasDePartilhados: number
  renomeados: number
  entidadesApagadas: number
  materiaisAlterados: number
  classificacoesAlteradas: number
  tipos: TipoEscolhido[]
  avisos: string[]
}

// ---------------------------------------------------------------------
// Tipos de valor
// ---------------------------------------------------------------------

const TEXTO = new Set([
  'IFCLABEL', 'IFCTEXT', 'IFCIDENTIFIER', 'IFCDESCRIPTIVEMEASURE', 'IFCURIREFERENCE',
  'IFCDATE', 'IFCDATETIME', 'IFCTIME', 'IFCDURATION', 'IFCTEXTFONTNAME', 'IFCPRESENTABLETEXT',
  'IFCGLOBALLYUNIQUEID', 'IFCFONTSTYLE', 'IFCFONTVARIANT', 'IFCFONTWEIGHT', 'IFCTEXTALIGNMENT',
  'IFCTEXTDECORATION', 'IFCTEXTTRANSFORMATION', 'IFCBOXALIGNMENT',
])
const BOOLEANO = new Set(['IFCBOOLEAN', 'IFCLOGICAL'])
const INTEIRO = new Set(['IFCINTEGER', 'IFCPOSITIVEINTEGER', 'IFCTIMESTAMP', 'IFCDAYINMONTHNUMBER',
  'IFCDAYINWEEKNUMBER', 'IFCMONTHINYEARNUMBER', 'IFCYEARNUMBER', 'IFCHOURINDAY', 'IFCMINUTEINHOUR'])

type Genero = 'texto' | 'booleano' | 'inteiro' | 'numero'

/** Que género de valor pede o tipo, ou null se não o conhecemos. */
export function generoDoTipo(tipo: string): Genero | null {
  const t = tipo.toUpperCase()
  if (!/^IFC[A-Z0-9]+$/.test(t)) return null
  if (TEXTO.has(t)) return 'texto'
  if (BOOLEANO.has(t)) return 'booleano'
  if (INTEIRO.has(t)) return 'inteiro'
  if (t.endsWith('MEASURE') || t === 'IFCREAL' || t === 'IFCNUMERICMEASURE' || t.endsWith('RATIO')) return 'numero'
  return null
}

/** "IfcLengthMeasure", "ifclengthmeasure" ou "LengthMeasure" -> "IFCLENGTHMEASURE". */
export function normalizarTipo(t: string): string {
  const u = t.trim().toUpperCase()
  return u.startsWith('IFC') ? u : `IFC${u}`
}

/** Número escrito à portuguesa ou à inglesa. Recusa separadores de
 *  milhares: "1.234" é ambíguo e não se adivinha. */
export function lerNumero(s: string): number | null {
  const t = s.trim().replace(/\s/g, '')
  if (!/^[-+]?\d+([.,]\d+)?([eE][-+]?\d+)?$/.test(t)) return null
  const n = Number(t.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

function lerBooleano(s: string, logico: boolean): string | null {
  const t = s.trim().toUpperCase()
  if (['T', 'TRUE', 'VERDADEIRO', 'SIM', '1', '.T.'].includes(t)) return '.T.'
  if (['F', 'FALSE', 'FALSO', 'NÃO', 'NAO', '0', '.F.'].includes(t)) return '.F.'
  if (logico && ['U', 'UNKNOWN', 'DESCONHECIDO', '.U.'].includes(t)) return '.U.'
  return null
}

/** O valor escrito como `IFCTIPO(...)`, ou null se não serve o tipo. */
export function codificarValor(tipo: string, valor: string): string | null {
  const t = tipo.toUpperCase()
  const genero = generoDoTipo(t)
  if (!genero) return null
  if (genero === 'texto') return `${t}(${argTexto(valor)})`
  if (genero === 'booleano') {
    const b = lerBooleano(valor, t === 'IFCLOGICAL')
    return b ? `${t}(${b})` : null
  }
  const n = lerNumero(valor)
  if (n === null) return null
  if (genero === 'inteiro') return Number.isInteger(n) ? `${t}(${n})` : null
  return `${t}(${stepReal(n)})`
}

// ---------------------------------------------------------------------
// Índices sobre o modelo
// ---------------------------------------------------------------------

interface Indices {
  /** objecto -> relações IfcRelDefinesByProperties que o incluem. */
  relsDoObjeto: Map<number, number[]>
  /** conjunto -> quantas ligações tem (objectos nas relações + tipos). */
  usosDoConjunto: Map<number, number>
  /** propriedade -> quantos conjuntos a referem. */
  usosDaPropriedade: Map<number, number>
  /** "pset\0prop" -> tipos de valor usados no modelo. */
  tiposPorNome: Map<string, Set<string>>
  /** "conjunto\0quantidade" -> classes de quantidade usadas no modelo. */
  quantidadesPorNome: Map<string, Set<string>>
  /** tipo -> instâncias (IfcRelDefinesByType). */
  instanciasDoTipo: Map<number, number[]>
  donoDaHistoria: string
}

const CLASSES_DE_CONJUNTO = ['IFCPROPERTYSET', 'IFCELEMENTQUANTITY']

/** Índice da lista de propriedades: HasProperties (4) no IfcPropertySet,
 *  Quantities (5) no IfcElementQuantity. */
function indiceDaLista(classe: string): number {
  return classe === 'IFCELEMENTQUANTITY' ? 5 : 4
}

function relatingDe(m: ModeloStep, rel: number): number[] {
  const a = m.args(rel)[5]
  const r = lerRef(a)
  return r !== null ? [r] : lerListaDeRefs(a)
}

/** O tipo de valor de uma propriedade, qualquer que seja a forma. */
function tipoDosValores(classe: string, a: string[]): string | null {
  if (classe === 'IFCPROPERTYSINGLEVALUE') return lerValorTipado(a[2])?.tipo ?? null
  if (classe === 'IFCPROPERTYLISTVALUE' || classe === 'IFCPROPERTYENUMERATEDVALUE') {
    return lerValorTipado(itensDaLista(a[2])[0])?.tipo ?? null
  }
  if (classe === 'IFCPROPERTYBOUNDEDVALUE') return lerValorTipado(a[2])?.tipo ?? lerValorTipado(a[3])?.tipo ?? null
  return null
}

function construirIndices(m: ModeloStep): Indices {
  const relsDoObjeto = new Map<number, number[]>()
  const usosDoConjunto = new Map<number, number>()
  const usosDaPropriedade = new Map<number, number>()
  const tiposPorNome = new Map<string, Set<string>>()
  const quantidadesPorNome = new Map<string, Set<string>>()
  for (const rel of m.idsDaClasse('IFCRELDEFINESBYPROPERTIES')) {
    const objs = lerListaDeRefs(m.args(rel)[4])
    for (const o of objs) {
      const arr = relsDoObjeto.get(o)
      if (arr) arr.push(rel)
      else relsDoObjeto.set(o, [rel])
    }
    for (const c of relatingDe(m, rel)) usosDoConjunto.set(c, (usosDoConjunto.get(c) ?? 0) + objs.length)
  }
  // Tipos: HasPropertySets no argumento 5 de qualquer IfcTypeObject.
  for (const id of m.todos()) {
    const classe = m.classe(id)!
    if (!classe.endsWith('TYPE') && !classe.endsWith('STYLE')) continue
    const a = m.args(id)
    if (a.length < 6 || !/^'.{22}'$/.test(a[0] ?? '')) continue
    for (const c of lerListaDeRefs(a[5])) usosDoConjunto.set(c, (usosDoConjunto.get(c) ?? 0) + 1)
  }
  for (const classe of CLASSES_DE_CONJUNTO) {
    for (const c of m.idsDaClasse(classe)) {
      const nome = lerArgTexto(m.args(c)[2]) ?? ''
      for (const p of lerListaDeRefs(m.args(c)[indiceDaLista(classe)])) {
        usosDaPropriedade.set(p, (usosDaPropriedade.get(p) ?? 0) + 1)
        const pa = m.args(p)
        const chave = `${nome}\0${lerArgTexto(pa[0]) ?? ''}`
        const cp = m.classe(p) ?? ''
        if (cp.startsWith('IFCQUANTITY')) {
          const q = quantidadesPorNome.get(chave)
          if (q) q.add(cp)
          else quantidadesPorNome.set(chave, new Set([cp]))
          continue
        }
        const tipo = tipoDosValores(cp, pa)
        if (!tipo) continue
        const s = tiposPorNome.get(chave)
        if (s) s.add(tipo)
        else tiposPorNome.set(chave, new Set([tipo]))
      }
    }
  }
  const instanciasDoTipo = new Map<number, number[]>()
  for (const rel of m.idsDaClasse('IFCRELDEFINESBYTYPE')) {
    const a = m.args(rel)
    const t = lerRef(a[5])
    if (t !== null) instanciasDoTipo.set(t, [...(instanciasDoTipo.get(t) ?? []), ...lerListaDeRefs(a[4])])
  }
  const oh = m.idsDaClasse('IFCOWNERHISTORY')[0]
  return {
    relsDoObjeto,
    usosDoConjunto,
    usosDaPropriedade,
    tiposPorNome,
    quantidadesPorNome,
    instanciasDoTipo,
    donoDaHistoria: oh !== undefined ? `#${oh}` : '$',
  }
}

function nomeDe(m: ModeloStep, id: number): string | null {
  const c = m.classe(id)
  if (!c) return null
  if (CLASSES_DE_CONJUNTO.includes(c)) return lerArgTexto(m.args(id)[2])
  return lerArgTexto(m.args(id)[0])
}

/** Um IfcTypeObject (IfcWallType, IfcDoorStyle...): tem GlobalId e a
 *  lista HasPropertySets no argumento 5. */
export function ehTipo(m: ModeloStep, id: number): boolean {
  const classe = m.classe(id)
  if (!classe || (!classe.endsWith('TYPE') && !classe.endsWith('STYLE'))) return false
  const a = m.args(id)
  return a.length >= 6 && /^'.{22}'$/.test(a[0] ?? '')
}

/** Uma ligação objecto -> conjunto: pela relação IfcRelDefinesByProperties
 *  (instâncias), ou pela lista HasPropertySets do próprio tipo (rel null). */
interface Ligacao {
  conjunto: number
  rel: number | null
}

/** Conjuntos (Pset/Qto) de um objecto: os de instância, ou, num tipo, os
 *  da sua lista HasPropertySets. */
function conjuntosDoObjeto(m: ModeloStep, ix: Indices, obj: number): Ligacao[] {
  if (ehTipo(m, obj)) return lerListaDeRefs(m.args(obj)[5]).map((conjunto) => ({ conjunto, rel: null }))
  const out: Ligacao[] = []
  for (const rel of ix.relsDoObjeto.get(obj) ?? []) {
    if (!m.existe(rel)) continue
    for (const c of relatingDe(m, rel)) out.push({ conjunto: c, rel })
  }
  return out
}

function conjuntosComNome(m: ModeloStep, nome: string): number[] {
  const out: number[] = []
  for (const classe of CLASSES_DE_CONJUNTO) {
    for (const c of m.idsDaClasse(classe)) if (lerArgTexto(m.args(c)[2]) === nome) out.push(c)
  }
  return out
}

/** Texto legível de um valor tipado: `IFCLABEL('EI30')` -> "EI30",
 *  `IFCBOOLEAN(.T.)` -> "TRUE", `IFCLENGTHMEASURE(3000.)` -> "3000". */
export function valorLegivel(arg: string | undefined): string | null {
  const v = lerValorTipado(arg)
  if (!v) return null
  const texto = lerArgTexto(v.interior)
  if (texto !== null) return texto
  if (v.interior === '.T.') return 'TRUE'
  if (v.interior === '.F.') return 'FALSE'
  if (v.interior === '.U.') return 'UNKNOWN'
  const n = Number(v.interior)
  return Number.isFinite(n) ? String(n) : v.interior
}

/** Os itens de topo de uma lista STEP: "(IFCLABEL('a'),IFCLABEL('b'))". */
function itensDaLista(arg: string | undefined): string[] {
  const t = (arg ?? '').trim()
  if (!t.startsWith('(') || !t.endsWith(')') || t === '()') return []
  return splitTopLevelArgs(t.slice(1, -1)).map((x) => x.trim()).filter(Boolean)
}

export interface ValorActual {
  valor: string | null
  tipo: string | null
  /** Id da entidade de onde veio, para a origem ficar à vista. */
  id: number
}

/** Leitor dos valores de instância (não os do tipo) de um modelo, com
 *  os índices construídos uma vez. */
export function criarLeitor(m: ModeloStep): (guid: string, pset: string, propriedade: string) => ValorActual | null {
  const ix = construirIndices(m)
  return (guid, pset, propriedade) => {
    const obj = m.porGuid(guid)
    if (obj === null) return null
    for (const { conjunto } of conjuntosDoObjeto(m, ix, obj)) {
      if (nomeDe(m, conjunto) !== pset) continue
      const classe = m.classe(conjunto)!
      for (const p of lerListaDeRefs(m.args(conjunto)[indiceDaLista(classe)])) {
        if (nomeDe(m, p) !== propriedade) continue
        const a = m.args(p)
        if (m.classe(p) === 'IFCPROPERTYSINGLEVALUE') {
          return { valor: valorLegivel(a[2]), tipo: lerValorTipado(a[2])?.tipo ?? null, id: p }
        }
        if (m.classe(p) === 'IFCPROPERTYENUMERATEDVALUE' || m.classe(p) === 'IFCPROPERTYLISTVALUE') {
          const vals = itensDaLista(a[2])
          return { valor: vals.length ? vals.map(valorLegivel).join(' | ') : null, tipo: lerValorTipado(vals[0])?.tipo ?? null, id: p }
        }
        if (m.classe(p) === 'IFCPROPERTYBOUNDEDVALUE') {
          // Escreve-se como se lê numa folha: mínimo..máximo.
          const max = valorLegivel(a[2]) ?? ''
          const min = valorLegivel(a[3]) ?? ''
          return { valor: min || max ? `${min}..${max}` : null, tipo: tipoDosValores('IFCPROPERTYBOUNDEDVALUE', a), id: p }
        }
        if (m.classe(p)?.startsWith('IFCQUANTITY')) {
          const n = Number(a[3])
          return { valor: Number.isFinite(n) ? String(n) : null, tipo: m.classe(p)!, id: p }
        }
        return { valor: null, tipo: m.classe(p) ?? null, id: p }
      }
    }
    return null
  }
}

// ---------------------------------------------------------------------
// Aplicar
// ---------------------------------------------------------------------

export function relatorioVazio(): Relatorio {
  return {
    aplicadas: 0,
    ignoradas: [],
    psetsCriados: 0,
    propriedadesCriadas: 0,
    valoresAlterados: 0,
    copiasDePartilhados: 0,
    renomeados: 0,
    entidadesApagadas: 0,
    materiaisAlterados: 0,
    classificacoesAlteradas: 0,
    tipos: [],
    avisos: [],
  }
}

export function aplicarOperacoes(
  m: ModeloStep,
  operacoes: Operacao[],
  opcoes?: { novoGuid?: () => string },
): Relatorio {
  const guid = opcoes?.novoGuid ?? (() => novoGlobalId())
  const r = relatorioVazio()
  let ix: Indices | null = null
  const indices = () => (ix ??= construirIndices(m))
  const tiposVistos = new Set<string>()
  const avisar = (a: string) => {
    if (!r.avisos.includes(a)) r.avisos.push(a)
  }
  if (m.esquema() === 'IFC2X3' && m.idsDaClasse('IFCOWNERHISTORY').length === 0) {
    avisar('O ficheiro é IFC2X3 e não tem IfcOwnerHistory, que o IFC2X3 exige nos Psets novos: ficam com $.')
  }

  const ignorar = (operacao: Operacao, motivo: string) => r.ignoradas.push({ operacao, motivo })

  /** Garante que o conjunto `c` ligado ao objecto `obj` só serve este
   *  objecto; se servir mais, faz uma cópia só para ele. */
  const soDoObjeto = (obj: number, c: number, rel: number | null): number => {
    const x = indices()
    if ((x.usosDoConjunto.get(c) ?? 0) <= 1) return c
    const a = m.args(c)
    const copia = m.criar(m.classe(c)!, [argTexto(guid()), ...a.slice(1)])
    for (const p of lerListaDeRefs(a[indiceDaLista(m.classe(c)!)])) {
      x.usosDaPropriedade.set(p, (x.usosDaPropriedade.get(p) ?? 0) + 1)
    }
    if (rel === null) {
      // Um tipo: troca-se a referência na sua lista HasPropertySets.
      const lista = lerListaDeRefs(m.args(obj)[5])
      m.definirArg(obj, 5, `(${lista.map((q) => `#${q === c ? copia : q}`).join(',')})`)
    } else if (lerListaDeRefs(m.args(rel)[4]).length === 1 && relatingDe(m, rel).length === 1) {
      // A relação já só tem este objecto: basta apontá-la para a cópia.
      m.definirArg(rel, 5, `#${copia}`)
    } else {
      const objs = lerListaDeRefs(m.args(rel)[4])
      m.definirArg(rel, 4, `(${objs.filter((o) => o !== obj).map((o) => `#${o}`).join(',')})`)
      const novaRel = m.criar('IFCRELDEFINESBYPROPERTIES', [
        argTexto(guid()), x.donoDaHistoria, '$', '$', `(#${obj})`, `#${copia}`,
      ])
      x.relsDoObjeto.set(obj, [...(x.relsDoObjeto.get(obj) ?? []).filter((q) => q !== rel), novaRel])
    }
    x.usosDoConjunto.set(c, (x.usosDoConjunto.get(c) ?? 1) - 1)
    x.usosDoConjunto.set(copia, 1)
    r.copiasDePartilhados++
    return copia
  }

  const escolherTipo = (
    op: Extract<Operacao, { tipo: 'definir' }>,
    existente: string | undefined,
  ): { tipo: string; porque: TipoEscolhido['porque'] } | { erro: string } => {
    if (op.tipoDeValor) {
      const t = normalizarTipo(op.tipoDeValor)
      if (!generoDoTipo(t)) return { erro: `Tipo de valor desconhecido: ${op.tipoDeValor}.` }
      return { tipo: t, porque: 'cabecalho' }
    }
    if (existente) return { tipo: existente, porque: 'existente' }
    const usados = indices().tiposPorNome.get(`${op.pset}\0${op.propriedade}`)
    if (usados && usados.size === 1) return { tipo: [...usados][0], porque: 'modelo' }
    if (usados && usados.size > 1) {
      return {
        erro: `O modelo usa tipos diferentes para ${op.pset}.${op.propriedade} (${[...usados].join(', ')}). Indica o tipo no cabeçalho, por exemplo "${op.pset}.${op.propriedade} [${[...usados][0]}]".`,
      }
    }
    return { tipo: 'IFCLABEL', porque: 'omissao' }
  }

  const registarTipo = (pset: string, propriedade: string, tipo: string, porque: TipoEscolhido['porque']) => {
    const chave = `${pset}\0${propriedade}\0${tipo}\0${porque}`
    if (tiposVistos.has(chave)) return
    tiposVistos.add(chave)
    r.tipos.push({ pset, propriedade, tipo, porque })
    if (generoDoTipo(tipo) === 'numero' && tipo !== 'IFCREAL' && tipo !== 'IFCNUMERICMEASURE') {
      avisar('As medidas novas ficam sem unidade própria: valem na unidade do projecto (IfcUnitAssignment) para esse tipo de medida.')
    }
  }

  /** A forma de uma propriedade que já existe, pela sua classe. */
  const formaDe = (classe: string): FormaDaPropriedade | 'enumerado' | null => {
    if (classe === 'IFCPROPERTYSINGLEVALUE') return 'simples'
    if (classe === 'IFCPROPERTYLISTVALUE') return 'lista'
    if (classe === 'IFCPROPERTYBOUNDEDVALUE') return 'intervalo'
    if (classe === 'IFCPROPERTYENUMERATEDVALUE') return 'enumerado'
    if (classe.startsWith('IFCQUANTITY')) return 'quantidade'
    return null
  }
  const ifc2x3 = m.esquema() === 'IFC2X3'

  /** Os argumentos novos de uma propriedade (ou quantidade), a partir dos
   *  actuais (ou de nenhuns, se for nova). Devolve o erro, se o valor não
   *  servir. `tipo` é o tipo de valor (ou a classe da quantidade). */
  const argumentosNovos = (
    forma: FormaDaPropriedade,
    tipo: string,
    nome: string,
    actuais: string[] | null,
    valor: string,
  ): { args: string[] } | { erro: string } => {
    if (forma === 'quantidade') {
      const n = lerNumero(valor)
      if (n === null) return { erro: `"${valor}" não é um número.` }
      // O IFC exige quantidades não negativas (regras das IfcQuantity*).
      if (n < 0) return { erro: `Uma quantidade não pode ser negativa ("${valor}").` }
      const base = actuais ?? (ifc2x3 ? [argTexto(nome), '$', '$', '0.'] : [argTexto(nome), '$', '$', '0.', '$'])
      return { args: base.map((a, i) => (i === 3 ? stepReal(n) : a)) }
    }
    if (forma === 'lista') {
      const itens = valor.split('|').map((v) => v.trim()).filter(Boolean)
      const cod = itens.map((v) => codificarValor(tipo, v))
      const mau = cod.findIndex((c) => c === null)
      if (mau >= 0) return { erro: `"${itens[mau]}" não serve para ${tipo}.` }
      const base = actuais ?? [argTexto(nome), '$', '()', '$']
      return { args: base.map((a, i) => (i === 2 ? `(${cod.join(',')})` : a)) }
    }
    if (forma === 'intervalo') {
      const iv = lerIntervalo(valor)
      if (!iv) return { erro: `"${valor}" não é um intervalo: escreve "mínimo..máximo" (um dos lados pode ficar vazio).` }
      const min = iv.min === null ? '$' : codificarValor(tipo, iv.min)
      const max = iv.max === null ? '$' : codificarValor(tipo, iv.max)
      if (min === null || max === null) return { erro: `"${valor}" não serve para ${tipo}.` }
      const nMin = iv.min === null ? null : lerNumero(iv.min)
      const nMax = iv.max === null ? null : lerNumero(iv.max)
      if (nMin !== null && nMax !== null && nMin > nMax) return { erro: `O mínimo (${iv.min}) é maior do que o máximo (${iv.max}).` }
      const base = actuais ?? (ifc2x3 ? [argTexto(nome), '$', '$', '$', '$'] : [argTexto(nome), '$', '$', '$', '$', '$'])
      // UpperBoundValue no 2, LowerBoundValue no 3, nos dois esquemas.
      return { args: base.map((a, i) => (i === 2 ? max : i === 3 ? min : a)) }
    }
    const c = codificarValor(tipo, valor)
    if (c === null) return { erro: `"${valor}" não serve para ${tipo}.` }
    const base = actuais ?? [argTexto(nome), '$', '$', '$']
    return { args: base.map((a, i) => (i === 2 ? c : a)) }
  }

  const CLASSE_DA_FORMA: Record<Exclude<FormaDaPropriedade, 'quantidade'>, string> = {
    simples: 'IFCPROPERTYSINGLEVALUE',
    lista: 'IFCPROPERTYLISTVALUE',
    intervalo: 'IFCPROPERTYBOUNDEDVALUE',
  }

  const definir = (op: Extract<Operacao, { tipo: 'definir' }>) => {
    const x = indices()
    const obj = m.porGuid(op.guid)
    if (obj === null) return ignorar(op, `Não há nenhum elemento com a GlobalId ${op.guid}.`)
    if (op.pset.trim() === '' || op.propriedade.trim() === '') return ignorar(op, 'Falta o nome do conjunto ou da propriedade.')
    const ligados = conjuntosDoObjeto(m, x, obj).filter((l) => nomeDe(m, l.conjunto) === op.pset)
    const alvo = ligados[0]
    const ehQto = alvo ? m.classe(alvo.conjunto) === 'IFCELEMENTQUANTITY' : op.forma === 'quantidade' || !!op.classeDaQuantidade
    const listaIdx = ehQto ? 5 : 4
    const refs = alvo ? lerListaDeRefs(m.args(alvo.conjunto)[listaIdx]) : []
    const existente = refs.find((p) => nomeDe(m, p) === op.propriedade)

    // A forma: a da propriedade que existe; senão a pedida; senão simples.
    let forma: FormaDaPropriedade
    if (existente !== undefined) {
      const f = formaDe(m.classe(existente) ?? '')
      if (f === null) return ignorar(op, `${op.pset}.${op.propriedade} é uma ${m.classe(existente)}: esta forma não se edita por folha.`)
      if (f === 'enumerado') return definirEnumerado(op, obj, alvo!, existente)
      if (op.forma && op.forma !== f) {
        return ignorar(op, `${op.pset}.${op.propriedade} já existe como ${f} no ficheiro, não como ${op.forma}.`)
      }
      forma = f
    } else {
      forma = ehQto ? 'quantidade' : (op.forma ?? 'simples')
      if (forma === 'quantidade' && !ehQto) return ignorar(op, `${op.pset} é um conjunto de propriedades: uma quantidade tem de ir para um IfcElementQuantity.`)
    }
    if (ehQto && forma !== 'quantidade') return ignorar(op, `${op.pset} é um conjunto de quantidades: só leva quantidades.`)

    // O tipo de valor (ou a classe da quantidade).
    let tipo: string
    let porque: TipoEscolhido['porque']
    if (forma === 'quantidade') {
      if (existente !== undefined) {
        tipo = m.classe(existente)!
        porque = 'existente'
      } else if (op.classeDaQuantidade) {
        tipo = normalizarTipo(op.classeDaQuantidade)
        if (!CLASSES_DE_QUANTIDADE.includes(tipo)) return ignorar(op, `${op.classeDaQuantidade} não é uma classe de quantidade (IfcQuantityLength, IfcQuantityArea...).`)
        porque = 'cabecalho'
      } else {
        const usadas = x.quantidadesPorNome.get(`${op.pset}\0${op.propriedade}`)
        if (!usadas || usadas.size !== 1) {
          return ignorar(op, `Para criar a quantidade ${op.pset}.${op.propriedade} indica a classe no cabeçalho, por exemplo "${op.pset}.${op.propriedade} [IfcQuantityArea]".`)
        }
        tipo = [...usadas][0]
        porque = 'modelo'
      }
    } else {
      const escolha = escolherTipo(op, existente !== undefined ? tipoDosValores(m.classe(existente)!, m.args(existente)) ?? undefined : undefined)
      if ('erro' in escolha) return ignorar(op, escolha.erro)
      if (forma === 'intervalo' && escolha.porque === 'omissao') {
        return ignorar(op, `Um intervalo precisa do tipo de valor: indica-o no cabeçalho, por exemplo "${op.pset}.${op.propriedade} [intervalo IfcLengthMeasure]".`)
      }
      tipo = escolha.tipo
      porque = escolha.porque
    }

    const r0 = argumentosNovos(forma, tipo, op.propriedade, existente !== undefined ? m.args(existente) : null, op.valor)
    if ('erro' in r0) return ignorar(op, r0.erro)
    const classeNova = forma === 'quantidade' ? tipo : CLASSE_DA_FORMA[forma]

    if (!alvo) {
      const prop = m.criar(classeNova, r0.args)
      const conjunto = ehQto
        ? m.criar('IFCELEMENTQUANTITY', [argTexto(guid()), x.donoDaHistoria, argTexto(op.pset), '$', '$', `(#${prop})`])
        : m.criar('IFCPROPERTYSET', [argTexto(guid()), x.donoDaHistoria, argTexto(op.pset), '$', `(#${prop})`])
      if (ehTipo(m, obj)) {
        // Num tipo, o conjunto entra na lista HasPropertySets (que pode
        // estar a $), sem relação.
        const lista = lerListaDeRefs(m.args(obj)[5])
        m.definirArg(obj, 5, `(${[...lista, conjunto].map((q) => `#${q}`).join(',')})`)
      } else {
        const rel = m.criar('IFCRELDEFINESBYPROPERTIES', [argTexto(guid()), x.donoDaHistoria, '$', '$', `(#${obj})`, `#${conjunto}`])
        x.relsDoObjeto.set(obj, [...(x.relsDoObjeto.get(obj) ?? []), rel])
      }
      x.usosDoConjunto.set(conjunto, 1)
      x.usosDaPropriedade.set(prop, 1)
      r.psetsCriados++
      r.propriedadesCriadas++
    } else if (existente !== undefined) {
      const actuais = m.args(existente)
      if (r0.args.every((a, i) => a === actuais[i])) {
        r.aplicadas++
        return
      }
      const conjunto = soDoObjeto(obj, alvo.conjunto, alvo.rel)
      if ((x.usosDaPropriedade.get(existente) ?? 0) > 1) {
        // Partilhada com outro conjunto: cópia só para este.
        const nova = m.criar(m.classe(existente)!, r0.args)
        const refsAgora = lerListaDeRefs(m.args(conjunto)[listaIdx])
        m.definirArg(conjunto, listaIdx, `(${refsAgora.map((p) => `#${p === existente ? nova : p}`).join(',')})`)
        x.usosDaPropriedade.set(existente, (x.usosDaPropriedade.get(existente) ?? 1) - 1)
        x.usosDaPropriedade.set(nova, 1)
        r.copiasDePartilhados++
      } else {
        m.definirArgs(existente, r0.args)
      }
      r.valoresAlterados++
    } else {
      const conjunto = soDoObjeto(obj, alvo.conjunto, alvo.rel)
      const prop = m.criar(classeNova, r0.args)
      const refsAgora = lerListaDeRefs(m.args(conjunto)[listaIdx])
      m.definirArg(conjunto, listaIdx, `(${[...refsAgora, prop].map((p) => `#${p}`).join(',')})`)
      x.usosDaPropriedade.set(prop, 1)
      r.propriedadesCriadas++
    }
    r.aplicadas++
    if (forma === 'quantidade') {
      avisar('As quantidades ficam sem unidade própria: valem na unidade do projecto (IfcUnitAssignment) para esse tipo de medida.')
    } else {
      registarTipo(op.pset, op.propriedade, tipo, porque)
    }
    if (ehTipo(m, obj)) avisarHeranca(obj, op)
  }

  /** Num tipo, o valor é herdado pelas instâncias, excepto pelas que têm
   *  valor próprio para a mesma propriedade (esse prevalece). Diz-se
   *  quantas são, uma vez por tipo e propriedade. */
  const herancasVistas = new Set<string>()
  const avisarHeranca = (tipo: number, op: Extract<Operacao, { tipo: 'definir' }>) => {
    const chave = `${tipo}\0${op.pset}\0${op.propriedade}`
    if (herancasVistas.has(chave)) return
    herancasVistas.add(chave)
    const x = indices()
    const instancias = x.instanciasDoTipo.get(tipo) ?? []
    let comValorProprio = 0
    for (const inst of instancias) {
      const tem = conjuntosDoObjeto(m, x, inst).some(
        (l) =>
          nomeDe(m, l.conjunto) === op.pset &&
          lerListaDeRefs(m.args(l.conjunto)[indiceDaLista(m.classe(l.conjunto)!)]).some((p) => nomeDe(m, p) === op.propriedade),
      )
      if (tem) comValorProprio++
    }
    const nome = lerArgTexto(m.args(tipo)[2]) ?? `#${tipo}`
    avisar(
      comValorProprio > 0
        ? `${op.pset}.${op.propriedade} no tipo "${nome}" (#${tipo}): ${instancias.length} instância(s) herdam-no, mas ${comValorProprio} têm valor próprio, que prevalece sobre o do tipo.`
        : `${op.pset}.${op.propriedade} no tipo "${nome}" (#${tipo}): herdado pelas ${instancias.length} instância(s) do tipo.`,
    )
  }

  /** Valor enumerado: só se aceitam os valores que a IfcPropertyEnumeration
   *  do ficheiro permite, escritos tal como lá estão. Vários valores
   *  separados por "|". Sem enumeração de referência, usa o tipo dos
   *  valores que já lá estão. */
  const definirEnumerado = (op: Extract<Operacao, { tipo: 'definir' }>, obj: number, alvo: Ligacao, prop: number) => {
    const x = indices()
    const a = m.args(prop)
    const pedidos = op.valor.split('|').map((v) => v.trim()).filter(Boolean)
    if (pedidos.length === 0) return ignorar(op, 'Sem valor.')
    const refEnum = lerRef(a[3])
    const literais: string[] = []
    let tipoUsado: string | null
    if (refEnum !== null && m.classe(refEnum) === 'IFCPROPERTYENUMERATION') {
      const permitidos = itensDaLista(m.args(refEnum)[1])
      const legiveis = permitidos.map(valorLegivel)
      for (const p of pedidos) {
        let i = legiveis.indexOf(p)
        if (i < 0) {
          const semCaixa = legiveis.map((v, k) => (v?.toLowerCase() === p.toLowerCase() ? k : -1)).filter((k) => k >= 0)
          if (semCaixa.length === 1) i = semCaixa[0]
        }
        if (i < 0) {
          return ignorar(op, `"${p}" não é um dos valores permitidos pela enumeração #${refEnum}: ${legiveis.join(', ')}.`)
        }
        literais.push(permitidos[i])
      }
      tipoUsado = lerValorTipado(permitidos[0])?.tipo ?? null
    } else {
      const actuais = itensDaLista(a[2])
      const tipo = op.tipoDeValor ? normalizarTipo(op.tipoDeValor) : lerValorTipado(actuais[0])?.tipo
      if (!tipo) return ignorar(op, `${op.pset}.${op.propriedade} é enumerada sem enumeração de referência nem valores: indica o tipo no cabeçalho.`)
      for (const p of pedidos) {
        const c = codificarValor(tipo, p)
        if (c === null) return ignorar(op, `"${p}" não serve para ${tipo}.`)
        literais.push(c)
      }
      tipoUsado = tipo
    }
    const novo = `(${literais.join(',')})`
    if (a[2] === novo) {
      r.aplicadas++
      return
    }
    const conjunto = soDoObjeto(obj, alvo.conjunto, alvo.rel)
    if ((x.usosDaPropriedade.get(prop) ?? 0) > 1) {
      const nova = m.criar('IFCPROPERTYENUMERATEDVALUE', a.map((v, i) => (i === 2 ? novo : v)))
      const refsAgora = lerListaDeRefs(m.args(conjunto)[4])
      m.definirArg(conjunto, 4, `(${refsAgora.map((p) => `#${p === prop ? nova : p}`).join(',')})`)
      x.usosDaPropriedade.set(prop, (x.usosDaPropriedade.get(prop) ?? 1) - 1)
      x.usosDaPropriedade.set(nova, 1)
      r.copiasDePartilhados++
    } else {
      m.definirArg(prop, 2, novo)
    }
    r.valoresAlterados++
    r.aplicadas++
    if (tipoUsado) registarTipo(op.pset, op.propriedade, tipoUsado, 'enumeracao')
    if (ehTipo(m, obj)) avisarHeranca(obj, op)
  }

  const renomearPset = (op: Extract<Operacao, { tipo: 'renomear-pset' }>) => {
    const para = op.para.trim()
    if (!para) return ignorar(op, 'O nome novo está vazio.')
    const alvos = conjuntosComNome(m, op.de)
    if (alvos.length === 0) return ignorar(op, `Não há nenhum conjunto chamado ${op.de}.`)
    if (/^(Pset|Qto)_/i.test(para) && !/^(Pset|Qto)_/i.test(op.de)) {
      avisar(`"${para}": os prefixos Pset_ e Qto_ estão reservados aos conjuntos normalizados da buildingSMART.`)
    }
    for (const c of alvos) m.definirArg(c, 2, argTexto(para))
    r.renomeados += alvos.length
    r.aplicadas++
    ix = null
  }

  const renomearPropriedade = (op: Extract<Operacao, { tipo: 'renomear-propriedade' }>) => {
    const para = op.para.trim()
    if (!para) return ignorar(op, 'O nome novo está vazio.')
    const conjuntos = conjuntosComNome(m, op.pset)
    const nossos = new Set(conjuntos)
    const x = indices()
    let n = 0
    for (const c of conjuntos) {
      const idx = indiceDaLista(m.classe(c)!)
      const refs = lerListaDeRefs(m.args(c)[idx])
      let mudou = false
      const novas = refs.map((p) => {
        if (nomeDe(m, p) !== op.de) return p
        n++
        // Partilhada com um conjunto de outro nome? Cópia com o nome novo.
        const usos = x.usosDaPropriedade.get(p) ?? 0
        const soNossos = m.quemReferencia(new Set([p])).every((q) => nossos.has(q))
        if (usos > 1 && !soNossos) {
          mudou = true
          return m.criar(m.classe(p)!, m.args(p).map((a, i) => (i === 0 ? argTexto(para) : a)))
        }
        m.definirArg(p, 0, argTexto(para))
        return p
      })
      if (mudou) m.definirArg(c, idx, `(${novas.map((p) => `#${p}`).join(',')})`)
    }
    if (n === 0) return ignorar(op, `Não há ${op.pset}.${op.de} no modelo.`)
    r.renomeados += n
    r.aplicadas++
    ix = null
  }

  const apagarPset = (op: Extract<Operacao, { tipo: 'apagar-pset' }>) => {
    const conjuntos = conjuntosComNome(m, op.nome)
    if (conjuntos.length === 0) return ignorar(op, `Não há nenhum conjunto chamado ${op.nome}.`)
    // As propriedades lêem-se antes: depois de apagado, o conjunto já
    // não diz quais eram.
    const propriedades = conjuntos.flatMap((c) => propriedadesDe(m, c, true))
    const res = m.apagar(conjuntos)
    if (res.apagados.length === 0) {
      return ignorar(op, `Não se pode apagar: ${op.nome} é referido por ${res.bloqueados.map((b) => `#${b.por} (${b.classeDeQuemReferencia})`).join(', ')}.`)
    }
    r.entidadesApagadas += res.apagados.length
    r.entidadesApagadas += apagarOrfas(m, propriedades)
    for (const b of res.bloqueados) avisar(`#${b.id} não foi apagado: é referido por #${b.por} (${b.classeDeQuemReferencia}).`)
    r.aplicadas++
    ix = null
  }

  const apagarPropriedade = (op: Extract<Operacao, { tipo: 'apagar-propriedade' }>) => {
    const conjuntos = conjuntosComNome(m, op.pset)
    const tiradas: number[] = []
    const vazios: number[] = []
    for (const c of conjuntos) {
      const idx = indiceDaLista(m.classe(c)!)
      const refs = lerListaDeRefs(m.args(c)[idx])
      const ficam = refs.filter((p) => nomeDe(m, p) !== op.nome)
      if (ficam.length === refs.length) continue
      tiradas.push(...refs.filter((p) => nomeDe(m, p) === op.nome))
      // A lista de propriedades é obrigatória: um conjunto sem nenhuma
      // não é válido e apaga-se.
      if (ficam.length === 0) vazios.push(c)
      else m.definirArg(c, idx, `(${ficam.map((p) => `#${p}`).join(',')})`)
    }
    if (tiradas.length === 0) return ignorar(op, `Não há ${op.pset}.${op.nome} no modelo.`)
    if (vazios.length > 0) r.entidadesApagadas += m.apagar(vazios).apagados.length
    r.entidadesApagadas += apagarOrfas(m, tiradas)
    r.aplicadas++
    ix = null
  }

  let ixAssoc: IndiceDeAssociacoes | null = null
  const assoc = () => (ixAssoc ??= construirIndiceDeAssociacoes(m))
  const contexto = () => ({ m, novoGuid: guid, donoDaHistoria: indices().donoDaHistoria })
  const material = (op: Extract<Operacao, { tipo: 'material' }>) => {
    const obj = m.porGuid(op.guid)
    if (obj === null) return ignorar(op, `Não há nenhum elemento com a GlobalId ${op.guid}.`)
    const res = associarMaterial(contexto(), assoc(), obj, op.material)
    if (res.erro) return ignorar(op, res.erro)
    res.avisos.forEach(avisar)
    if (res.mudou) r.materiaisAlterados++
    r.aplicadas++
  }
  const classificarOp = (op: Extract<Operacao, { tipo: 'classificar' }>) => {
    const obj = m.porGuid(op.guid)
    if (obj === null) return ignorar(op, `Não há nenhum elemento com a GlobalId ${op.guid}.`)
    const res = classificar(contexto(), assoc(), obj, op.sistema, op.codigo, op.nome)
    if (res.erro) return ignorar(op, res.erro)
    res.avisos.forEach(avisar)
    if (res.mudou) r.classificacoesAlteradas++
    r.aplicadas++
  }

  for (const op of operacoes) {
    switch (op.tipo) {
      case 'material':
        material(op)
        break
      case 'classificar':
        classificarOp(op)
        break
      case 'definir':
        definir(op)
        break
      case 'renomear-pset':
        renomearPset(op)
        break
      case 'renomear-propriedade':
        renomearPropriedade(op)
        break
      case 'apagar-pset':
        apagarPset(op)
        break
      case 'apagar-propriedade':
        apagarPropriedade(op)
        break
    }
  }
  return r
}

/** Propriedades de um conjunto (e, com `fundo`, as filhas das
 *  propriedades complexas). */
function propriedadesDe(m: ModeloStep, conjunto: number, fundo: boolean): number[] {
  const classe = m.classe(conjunto)
  if (!classe) return []
  const out: number[] = []
  const visitar = (p: number) => {
    out.push(p)
    if (fundo && m.classe(p) === 'IFCCOMPLEXPROPERTY') for (const f of lerListaDeRefs(m.args(p)[3])) visitar(f)
  }
  for (const p of lerListaDeRefs(m.args(conjunto)[indiceDaLista(classe)])) visitar(p)
  return out
}

/** Apaga as que já ninguém refere. Devolve quantas. */
function apagarOrfas(m: ModeloStep, candidatas: number[]): number {
  const vivas = [...new Set(candidatas)].filter((p) => m.existe(p))
  if (vivas.length === 0) return 0
  const conjunto = new Set(vivas)
  const quemRefere = new Map<number, number[]>()
  for (const quem of m.quemReferencia(conjunto)) {
    for (const a of m.args(quem)) {
      for (const x of a.matchAll(/#(\d+)/g)) {
        const alvo = parseInt(x[1], 10)
        if (conjunto.has(alvo)) quemRefere.set(alvo, [...(quemRefere.get(alvo) ?? []), quem])
      }
    }
  }
  // Uma candidata é órfã se só a referem outras órfãs (a filha de uma
  // propriedade complexa que também vai). Ponto fixo.
  const orfas = new Set(vivas)
  for (let mudou = true; mudou; ) {
    mudou = false
    for (const p of [...orfas]) {
      if ((quemRefere.get(p) ?? []).some((q) => !orfas.has(q))) {
        orfas.delete(p)
        mudou = true
      }
    }
  }
  return orfas.size > 0 ? m.apagar(orfas).apagados.length : 0
}
